import {
  AlertTriangle,
  BellOff,
  Calendar,
  ChevronDown,
  Clock,
  Pencil,
} from "lucide-react";
import { Link } from "react-router-dom";
import { MARKETING_ROUTES } from "@/marketing/routes";

import { cn } from "@/marketing/lib/utils";
import { useCampaignQuietStore } from "@/marketing/stores/useCampaignQuietStore";
import type { InfluencerCampaign } from "@/marketing/models/influencerCampaign";
import type { CampaignInfluencer } from "@/marketing/models/campaignInfluencer";
import type { CampaignPipelineDefinition } from "@/marketing/models/campaignPipeline";
import { countByMilestone, getStageDefinition } from "@/marketing/lib/campaignPipeline";
import { formatDate, initials } from "@/marketing/lib/formatters";
import { useBrandLookup } from "@/marketing/components/brand/useBrandLookup";

const SHIPPED_STAGE = "Shipped";

interface CampaignDetailHeaderProps {
  campaign: InfluencerCampaign;
  pipelineDefinition: CampaignPipelineDefinition;
  influencers: CampaignInfluencer[];
  showInfluencerKpis?: boolean;
  onJumpToTab: (tab: "creators" | "activity" | "overview") => void;
  onEdit: () => void;
}

function dueState(postingDueDate?: string): {
  tone: "red" | "amber" | "none";
  label: string;
} {
  if (!postingDueDate) return { tone: "none", label: "—" };
  const due = new Date(postingDueDate).getTime();
  const now = Date.now();
  const diffDays = Math.round((due - now) / (1000 * 60 * 60 * 24));
  if (diffDays < 0)
    return { tone: "red", label: `${Math.abs(diffDays)} days overdue` };
  if (diffDays === 0) return { tone: "amber", label: "Due today" };
  if (diffDays <= 2) return { tone: "amber", label: `${diffDays} days left` };
  return { tone: "none", label: `${diffDays} days left` };
}

export function CampaignDetailHeader({
  campaign,
  pipelineDefinition,
  influencers,
  showInfluencerKpis = true,
  onJumpToTab,
  onEdit,
}: CampaignDetailHeaderProps) {
  const lookupBrand = useBrandLookup();
  const isQuiet = useCampaignQuietStore((s) => s.isQuiet(campaign.campaignId));
  const toggleQuiet = useCampaignQuietStore((s) => s.toggleQuiet);
  const brandName =
    lookupBrand(campaign.brandId)?.name ?? campaign.brandId.slice(0, 8);
  const due = dueState(campaign.postingDueDate);
  const overdue = due.tone === "red";

  const onboarded = countByMilestone(
    pipelineDefinition,
    influencers,
    "seat_filled",
  );
  // Cumulative: content_posted stays on every later stage — matches the dashboard postedCount.
  const postsLive = countByMilestone(
    pipelineDefinition,
    influencers,
    "content_posted",
  );
  const hasShippingStage =
    getStageDefinition(pipelineDefinition, SHIPPED_STAGE) !== undefined;
  const onboardingTarget = campaign.onboardingTarget ?? 0;
  const postTarget = campaign.postTarget ?? 0;
  const onbPct =
    onboardingTarget > 0 ? Math.round((onboarded / onboardingTarget) * 100) : 0;
  const postPct =
    postTarget > 0 ? Math.round((postsLive / postTarget) * 100) : 0;

  const ownerInitials = initials(campaign.ownerId);

  return (
    <header
      className={cn(
        "tw-relative tw-border-b tw-border-border tw-bg-background tw-px-8 tw-pt-3.5",
        overdue &&
          "before:tw-absolute before:tw-left-0 before:tw-top-0 before:tw-bottom-0 before:w-[3px] before:tw-bg-red-500",
      )}
    >
      {/* Row 1 — breadcrumb + status pill */}
      <div className="tw-flex tw-items-center tw-justify-between tw-text-xs tw-text-muted-foreground">
        <div className="tw-flex tw-items-center tw-gap-1.5 tw-truncate">
          <Link to={MARKETING_ROUTES.campaignMatcher} className="hover:tw-text-foreground">
            Campaign Manager
          </Link>
          <span className="tw-text-muted-foreground/60">/</span>
          <span>{brandName}</span>
          <span className="tw-text-muted-foreground/60">/</span>
          <span className="tw-truncate tw-font-medium tw-text-foreground">
            {campaign.name}
          </span>
        </div>
        <div className="tw-flex tw-items-center tw-gap-2">
          <button
            type="button"
            onClick={onEdit}
            className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-text-foreground hover:tw-bg-accent"
          >
            <Pencil className="tw-h-3 tw-w-3" />
            Edit campaign
          </button>
          <button
            type="button"
            className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-amber-100 tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-text-amber-800 hover:tw-bg-amber-200"
          >
            <span className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-amber-500" />
            {campaign.status}
            <ChevronDown className="tw-h-3 tw-w-3 tw-opacity-60" />
          </button>
          <button
            type="button"
            onClick={() => toggleQuiet(campaign.campaignId)}
            aria-pressed={isQuiet}
            title="When on, moving cards sends no notifications (email/push/in-app) to influencers"
            className={cn(
              "tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-transition-colors",
              isQuiet
                ? "tw-border-amber-300 tw-bg-amber-50 tw-text-amber-800"
                : "tw-border-border tw-bg-background tw-text-muted-foreground hover:tw-bg-muted",
            )}
          >
            <BellOff className="tw-h-3 tw-w-3" />
            Quiet mode {isQuiet ? "on" : "off"}
          </button>
        </div>
      </div>

      {/* Row 2 — title + owner + schedule pills */}
      <div className="tw-flex tw-items-start tw-justify-between tw-gap-6 tw-py-3">
        <div className="tw-min-w-0 tw-flex-1">
          <h1 className="tw-text-xl tw-font-semibold tw-leading-tight tw-tracking-tight">
            {campaign.name}
          </h1>
          <div className="tw-mt-2 tw-flex tw-items-center tw-gap-3 tw-text-xs">
            <div className="tw-flex tw-items-center tw-gap-2">
              <div className="tw-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-full tw-bg-violet-600 tw-text-[10px] tw-font-semibold tw-text-white">
                {ownerInitials}
              </div>
              <div className="tw-leading-tight">
                <strong className="tw-block tw-text-xs tw-font-medium tw-text-foreground">
                  {campaign.ownerId}
                </strong>
                <span className="tw-text-muted-foreground">Campaign Manager</span>
              </div>
            </div>
            <span className="tw-text-muted-foreground/60">·</span>
            <span className="tw-text-muted-foreground">{brandName}</span>
          </div>
        </div>
        <div className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-2">
          <div className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-2.5 tw-py-1 tw-text-[11px]">
            <Calendar className="tw-h-3 tw-w-3 tw-text-muted-foreground" />
            <span className="tw-font-semibold tw-uppercase tw-tracking-wider tw-text-muted-foreground">
              Window
            </span>
            <span>
              {formatDate(campaign.startAt)} → {formatDate(campaign.endAt)}
            </span>
          </div>
          <div
            className={cn(
              "tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-md tw-border tw-px-2.5 tw-py-1 tw-text-[11px]",
              due.tone === "red" && "tw-border-red-200 tw-bg-red-50 tw-text-red-700",
              due.tone === "amber" &&
                "tw-border-amber-200 tw-bg-amber-50 tw-text-amber-800",
              due.tone === "none" && "tw-border-border tw-bg-background",
            )}
          >
            {overdue ? (
              <AlertTriangle className="tw-h-3 tw-w-3" />
            ) : (
              <Clock className="tw-h-3 tw-w-3" />
            )}
            <span className="tw-font-semibold tw-uppercase tw-tracking-wider">
              Posting due
            </span>
            <span>
              {formatDate(campaign.postingDueDate)} · {due.label}
            </span>
          </div>
        </div>
      </div>

      {/* Row 3 — KPI strip */}
      {showInfluencerKpis && <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-pb-3 tw-text-sm">
        <button
          type="button"
          onClick={() => onJumpToTab("creators")}
          className="tw-inline-flex tw-items-baseline tw-gap-1 tw-text-foreground hover:tw-underline"
        >
          <span className="tw-font-semibold tw-tabular-nums tw-text-blue-600">
            {onboarded}/{onboardingTarget}
          </span>
          <span className="tw-text-muted-foreground">onboarded · {onbPct}%</span>
        </button>
        <span className="tw-text-muted-foreground/50">·</span>
        <button
          type="button"
          onClick={() => onJumpToTab("creators")}
          className="tw-inline-flex tw-items-baseline tw-gap-1 tw-text-foreground hover:tw-underline"
        >
          <span className="tw-font-semibold tw-tabular-nums tw-text-blue-600">
            {postsLive}/{postTarget}
          </span>
          <span className="tw-text-muted-foreground">posts live · {postPct}%</span>
        </button>
        {hasShippingStage && (
          <>
            <span className="tw-text-muted-foreground/50">·</span>
            <button
              type="button"
              onClick={() => onJumpToTab("overview")}
              className="tw-inline-flex tw-items-baseline tw-gap-1 tw-text-foreground hover:tw-underline"
            >
              <span className="tw-font-semibold tw-tabular-nums">
                {
                  influencers.filter(
                    (c) =>
                      !c.rejected &&
                      !c.shippingTracking &&
                      c.pipelineStage === SHIPPED_STAGE,
                  ).length
                }
              </span>
              <span className="tw-text-muted-foreground">
                shipments awaiting confirmation
              </span>
            </button>
          </>
        )}
        <span className="tw-text-muted-foreground/50">·</span>
        <button
          type="button"
          onClick={() => onJumpToTab("activity")}
          className="tw-inline-flex tw-items-baseline tw-gap-1 tw-text-foreground hover:tw-underline"
        >
          <span className="tw-font-semibold tw-tabular-nums">
            {influencers.filter((c) => c.rejected).length}
          </span>
          <span className="tw-text-muted-foreground">rejected</span>
        </button>
      </div>}
    </header>
  );
}
